import React from 'react';
import {
    Button, Checkbox, Chip, createStyles, FormControl, FormControlLabel, FormLabel,
    Grid, makeStyles, TextField, Theme
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { AuthContext } from '../../Authentication/AuthContext';
import ProfileService from '../../../Services/ProfileService';
import { ProfileModel, profileModelInitial } from '../../../Models/ProfileModel';
import { ClientModel } from '../../../Models/ClientModel';
import { UserContext } from '../UserContext';
import ClientService from '../../../Services/ClientService';
import { IErrorResponse } from '../../../shared/ErrorResponse';
import { ErrorHelper } from '../../../shared/ErrorHelper';
import UserService from '../../../Services/UserService';
import { UserModel, userModelInitial } from '../../../Models/UserModel';
import { validateEmail, validateSpecialCharacter } from '../../../shared/operators';
import { HomeContext } from '../../Home/HomeContext';
import { PageLocation } from '../../../shared/enum/PageLocation';
import { BasicConfirmDialog } from '../../../shared/components/BasicConfirmDialog';
import { EditAction } from '../../../shared/enum/EditAction';
import { BasicPage } from '../../../shared/components/BasicPage';
import { AxiosError, AxiosResponse } from 'axios';

const useStyles = makeStyles((theme: Theme) => createStyles({
    root: {
        padding: theme.spacing(3)
    },
    itens: {
        width: '100%',
        color: 'primary',
    },
    paper: {
        padding: theme.spacing(3),
    },
    gridTitle: {
        paddingBottom: theme.spacing(1),
    }
}));

export function UserInput() {
    const classes = useStyles();

    const authContext = React.useContext(AuthContext);
    const homeContext = React.useContext(HomeContext);
    const userContext = React.useContext(UserContext);

    const [hasSpecialCharacter, setHasSpecialCharacter] = React.useState(false);
    const [usernameNotDefinedError, setUsernameNotDefinedError] = React.useState(false);
    const [profileNotDefinedError, setProfileNotDefinedError] = React.useState(false);
    const [emailError, setEmailError] = React.useState(false);

    const [emailExistsErrorMessage, setEmailExistsErrorMessage] = React.useState('');
    const [isClientIdNotDefinedErrorMessage, setIsClientIdNotDefinedErrorMessage] = React.useState('');

    const [profileList, setProfileList] = React.useState<ProfileModel[]>([]);
    const [clientList, setClientList] = React.useState<ClientModel[]>([]);

    const [profileSelect, setProfileSelect] = React.useState<ProfileModel | null>(profileModelInitial);

    const [userClients, setUserClients] = React.useState<ClientModel[]>([]);

    const [edit, setEdit] = React.useState(false);
    const [confirmEdit, setConfirmEdit] = React.useState(false);
    const [confirmReset, setConfirmReset] = React.useState(false);

    React.useEffect(() => {
        fetchClients();
        fetchProfile();

        if (homeContext.pageLocation === PageLocation.USER_EDIT && userContext.user.id > 0) {
            fetchEditUser();
            setEdit(true);
        } else
            setEdit(false);
    }, []);

    React.useEffect(() => {
        if (homeContext.pageLocation != PageLocation.USER_EDIT)
            userContext.setUser(userModelInitial);
    }, [homeContext.pageLocation]);

    const sortClients = (a: ClientModel, b: ClientModel) => {
        return a.name.localeCompare(b.name);
    };

    const fetchClients = () => {
        homeContext.setOpenLoadingDialog(true);

        ClientService.clientService()
            .getAll()
            .then((res: AxiosResponse) => {
                if (res.data)
                    setClientList(res.data);
                else
                    setClientList([]);
            })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Cliente. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchProfile = () => {
        homeContext.setOpenLoadingDialog(true);

        ProfileService.profileService()
            .getAll()
            .then((res: AxiosResponse) => { setProfileList(res.data); })
            .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Perfil. <br/>', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const fetchEditUser = () => {
        if (userContext.user) {
            homeContext.setOpenLoadingDialog(true);

            UserService.userService()
                .get(userContext.user.id)
                .then((res: AxiosResponse) => {
                    if (res.data) {
                        const user = res.data as UserModel;
                        user.profileId = res.data.profile.id;
                        const sortedClients = user.clients ? user.clients.sort((a, b) => sortClients(a, b)) : null;

                        userContext.setUser(user);

                        setUserClients(sortedClients ?? []);
                        setProfileSelect(res.data.profile);
                    }
                })
                .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Usuário editado. <br/>', error); })
                .finally(() => { homeContext.setOpenLoadingDialog(false); });
        }
    };

    const resetEmailErrorMessages = () => {
        if (emailError)
            setEmailError(false);
        if (emailExistsErrorMessage)
            setEmailExistsErrorMessage('');
    };

    const hasError = () => {
        const usernameNotDefinedError = !userContext.user.username;
        const profileNotDefinedError = !userContext.user.profile;
        const hasSpecialCharacterError = validateSpecialCharacter(userContext.user.username || '');
        const emailError =
            !userContext.user.email ||
            !validateEmail(userContext.user.email);

        setUsernameNotDefinedError(usernameNotDefinedError);
        setProfileNotDefinedError(profileNotDefinedError);
        setHasSpecialCharacter(hasSpecialCharacterError);
        setEmailError(emailError);

        return usernameNotDefinedError || profileNotDefinedError || hasSpecialCharacterError || emailError;
    };

    const handleSave = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);

            const userData = userContext.user;
            userData.userId = authContext.login.user.id;

            if (edit) {
                UserService.userService().update(userContext.user)
                    .then(() => {
                        homeContext.showSuccess('Usuário alterado com sucesso!');
                        clearInputs();
                        homeContext.setPageLocation(PageLocation.USER_SEARCH);
                    })
                    .catch((error) => {
                        homeContext.showError('Ops... ocorreu um erro e não foi possível editar o usuário.' +
                            'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                    })
                    .finally(() => { homeContext.setOpenLoadingDialog(false); });
            } else {
                UserService.userService()
                    .create(userData)
                    .then(() => {
                        homeContext.showSuccess('O novo usuário foi incluído com sucesso!');
                        clearInputs();
                    })
                    .catch((error: AxiosError) => {
                        const errorResponse: IErrorResponse = ErrorHelper(error);

                        const errorEmail = errorResponse.errorList.find(e => e.property === 'E-mail');
                        const errorClients = errorResponse.errorList.find(e => e.property === 'Clients');

                        if (errorEmail || errorClients) {
                            setEmailExistsErrorMessage(errorEmail ? errorEmail.message : '');
                            setIsClientIdNotDefinedErrorMessage(errorClients ? errorClients.message : '');
                        }
                        else {
                            homeContext.showError('Ops... ocorreu um erro e não foi possível salvar o novo usuário.' +
                                'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                        }

                    })
                    .finally(() => { homeContext.setOpenLoadingDialog(false); });
            }
        }
    };
    const handleResetPassword = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);

            const userData = userContext.user;
            userData.userId = authContext.login.user.id;
            UserService.userService().resetPassword(userContext.user)
                .then(() => {
                    homeContext.showSuccess('Senha redefinida com sucesso!');
                    clearInputs();
                    homeContext.setPageLocation(PageLocation.USER_SEARCH);
                })
                .catch((error) => {
                    homeContext.showError('Ops... ocorreu um erro e não foi possível resetar a senha do usuário.' +
                        'Por favor, tente novamente dentro de alguns instantes. <br/>', error);
                })
                .finally(() => { homeContext.setOpenLoadingDialog(false); });
        }
    };

    const handleDeleteClient = (client: ClientModel) => {

        if (userContext.user.clients) {
            const selectedClients: ClientModel[] = [...userContext.user.clients];
            const index = selectedClients.findIndex(sc => sc.id === client.id);

            if (index > -1 && selectedClients[index].editAction != EditAction.INSERT)
                selectedClients[index].editAction = EditAction.DELETE;
            else
                selectedClients.splice(index, 1);

            setUserClients(selectedClients);
            userContext.setUser({ ...userContext.user, clients: selectedClients });
        }

    };

    const handleAddClient = (client: ClientModel) => {
        let selectedClients: ClientModel[] = [];

        if (userContext.user.clients) {
            selectedClients = [...userContext.user.clients];
            const index = selectedClients.findIndex(sc => sc.id === client.id);

            if (index > -1 && selectedClients[index].editAction === EditAction.DELETE) {
                selectedClients[index].editAction = EditAction.NONE;
            }
            else {
                client.editAction = EditAction.INSERT;
                selectedClients.push(client);
            }
        }
        else {
            selectedClients.push(client);
        }

        const sortedClients = selectedClients.sort((a, b) => sortClients(a, b));

        setUserClients(sortedClients);
        userContext.setUser({ ...userContext.user, clients: sortedClients });
    };

    const handleDeleteAllClients = () => {
        if (userContext.user.clients) {
            const selectedClientList: ClientModel[] = [...userContext.user.clients];

            const filteredClients = selectedClientList
                .filter((c: ClientModel) => c.editAction != EditAction.INSERT)
                .map((fc: ClientModel) => {
                    fc.editAction = EditAction.DELETE;
                    return fc;
                });

            setUserClients(filteredClients);
            userContext.setUser({ ...userContext.user, clients: filteredClients });
        }
    };

    const clearInputs = () => {
        userContext.setUser(userModelInitial);
        resetEmailErrorMessages();
        setProfileSelect(profileModelInitial);
        setUserClients([]);
    };

    const getName = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    label="Nome do usuário"
                    variant="outlined"
                    fullWidth
                    value={userContext.user.username ? userContext.user.username : ''}
                    onChange={(event) => userContext.setUser({ ...userContext.user, username: event.target.value })}
                    className={classes.itens}
                    inputProps={{ maxLength: 200 }}
                    error={(usernameNotDefinedError && !userContext.user.username) || hasSpecialCharacter}
                    helperText={(usernameNotDefinedError && !userContext.user.username ? 'Campo obrigatório' : '')
                        || (hasSpecialCharacter ? 'Caracteres especiais não são permitidos' : '')} />
            </Grid>
        );
    };

    const getEmail = () => {
        return (
            <Grid item xs={12} sm={6}>
                <TextField
                    label="E-mail do usuário"
                    variant="outlined"
                    fullWidth
                    value={userContext.user.email ? userContext.user.email : ''}
                    onChange={(event) => {
                        userContext.setUser({ ...userContext.user, email: event.target.value });
                        resetEmailErrorMessages();
                    }}
                    className={classes.itens}
                    inputProps={{ maxLength: 255 }}
                    error={emailError || emailExistsErrorMessage.length > 0}
                    helperText={(emailError
                        ? (userContext.user.email
                            ? 'O e-mail inserido não é válido'
                            : 'Campo Obrigatório')
                        : '') || emailExistsErrorMessage} />
            </Grid>
        );
    };

    const getProfile = () => {
        return (
            <Grid item xs={12} sm={6}>
                <Autocomplete
                    id="user-profile"
                    value={profileSelect}
                    onChange={(_, value: ProfileModel | null) => {
                        setProfileSelect(value);
                        userContext.setUser({ ...userContext.user, profile: value, profileId: value ? value.id : 0 });
                    }}
                    options={profileList}
                    getOptionLabel={(option: ProfileModel) => option.description}
                    className={classes.itens}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Perfil do usuário"
                            variant="outlined"
                            error={profileNotDefinedError && !userContext.user.profile}
                            helperText={profileNotDefinedError && !userContext.user.profile ? 'Campo obrigatório' : ''} />
                    } />
            </Grid>
        );
    };

    const getState = () => {
        return (
            <Grid item xs={12} sm={6}>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Status</FormLabel>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={userContext.user.isActive}
                                onChange={() => userContext.setUser({ ...userContext.user, isActive: !userContext.user.isActive })}
                                name="Ativo"
                                color="primary" />
                        }
                        label="Ativo" />
                </FormControl>
            </Grid>
        );
    };

    const getClients = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    filterSelectedOptions
                    id='user-clients'
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    className={classes.itens}
                    value={userClients.filter(uc => uc.editAction != EditAction.DELETE)}
                    options={clientList ? clientList : []}
                    getOptionLabel={(option: ClientModel) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    onChange={(_, __, reason, detail) => {
                        if (detail && detail.option) {
                            if (reason === 'remove-option')
                                handleDeleteClient(detail.option);
                            else if (reason === 'select-option')
                                handleAddClient(detail.option);
                        } else if (reason === 'clear') {
                            handleDeleteAllClients();
                        }
                    }}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) =>
                        <TextField {...params}
                            label="Clientes"
                            variant="outlined"
                            error={isClientIdNotDefinedErrorMessage.length > 0}
                            helperText={isClientIdNotDefinedErrorMessage} />
                    } />
            </Grid>
        );
    };

    const getSaveButton = () => {
        return (
            <Grid item xs={8} sm={(!edit) ? 12 : 6}>
                <Grid
                    container
                    direction="column"
                    justify="flex-end"
                    alignItems="flex-end">
                    <Button
                        onClick={() => edit ? setConfirmEdit(true) : handleSave()}
                        variant='contained'
                        color='primary'
                        size='large' >
                        {(!edit) ? 'Salvar usuário' : 'Salvar alterações'}
                    </Button>
                </Grid>
            </Grid>
        );
    };

    const getResetPasswordButton = () => {
        return (
            <>
                <BasicConfirmDialog
                    title="Tem certeza de que deseja redefinir a senha deste usuário?"
                    description="Ao confirmar, a senha do usuario será redefinida."
                    handleFirstButton={() => setConfirmReset(false)}
                    handleSecondButton={() => { handleResetPassword(); setConfirmReset(false); }}
                    textFirstButton="Cancelar"
                    textSecondButton="Confirmar"
                    open={confirmReset} />

                <Grid item xs={8} sm={(!edit) ? 12 : 6}>
                    <Grid
                        container
                        direction="column"
                        justify="flex-start"
                        alignItems="flex-start">
                        <Button
                            onClick={() => edit ? setConfirmReset(true) : handleResetPassword()}
                            variant='contained'
                            color='primary'
                            size='large' >
                            {'Redefinir Senha'}
                        </Button>
                    </Grid>
                </Grid>
            </>
        );
    };

    return (
        <BasicPage
            title={edit ? 'Edição de usuário' : 'Cadastro de usuário'}
            subtitle={edit
                ? 'Edite os campos que deseja alterar para o usuário.'
                : 'Para cadastrar um novo usuário, insira os dados solicitados abaixo.'}
            dialogs={
                <BasicConfirmDialog
                    title="Confirmar as alterações deste usuário?"
                    description="Ao confirmar, as alterações serão aplicadas."
                    handleFirstButton={() => setConfirmEdit(false)}
                    handleSecondButton={() => { handleSave(); setConfirmEdit(false); }}
                    textFirstButton="Cancelar"
                    textSecondButton="Confirmar"
                    open={confirmEdit} />}>
            <>
                {getName()}
                {getEmail()}
                {getProfile()}
                {edit && getState()}
                {getClients()}
                {(!edit) ? '' : getResetPasswordButton()}
                {getSaveButton()}
            </>
        </BasicPage>
    );
}